import { BadgeCheck, UserCheck } from "lucide-react";
import { Badge, Card, CardBody, EmptyState, PageHeader } from "@fellowflats/ui";
import { OkSchema } from "@fellowflats/contracts/account-writes";
import { RevokeReplySchema, VerificationsSchema } from "@fellowflats/contracts/admin";
import { api } from "../lib/api";
import { config } from "../lib/config";
import { useApi } from "../lib/use-api";
import { ActionButton, Loaded, NotesAction, day } from "../components/bits";

/**
 * The queues that decide whether anyone can publish (landlords) and who shows
 * the verified badge (listings). Oldest first: a queue, not a stack.
 */
export function Verifications() {
  const state = useApi(() => api("GET", "/admin/verifications", VerificationsSchema));
  const act = (p: Promise<unknown>) => p.then(state.reload);

  return (
    <div className="space-y-10">
      <PageHeader title="Verifications" description="Landlords cannot publish until they are verified. Listing badges are a badge, not a gate." />
      <Loaded state={state}>
        {(v) => (
          <>
            <section className="space-y-3">
              <h2 className="text-lg font-semibold">Landlords waiting ({v.pendingLandlords.length})</h2>
              {v.pendingLandlords.length === 0 ? (
                <EmptyState icon={UserCheck} title="Nobody waiting" body="Landlords join this queue at onboarding and when they save a listing." />
              ) : (
                v.pendingLandlords.map((l) => (
                  <Card key={l.landlordProfileId}>
                    <CardBody className="space-y-3 p-5">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-semibold">{l.fullName ?? l.email}</p>
                        {l.companyName && <Badge>{l.companyName}</Badge>}
                        <Badge tone={l.emailVerified ? "success" : "warning"}>{l.emailVerified ? "email confirmed" : "email not confirmed"}</Badge>
                        <Badge tone={l.phoneVerified ? "success" : "neutral"}>
                          {l.phoneVerified ? "phone verified" : l.phone ? `phone ${l.phone}` : "no phone"}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {l.email} · joined {day(l.joinedAt)} · waiting since {day(l.requestedAt)}
                      </p>
                      {l.listings.length > 0 && (
                        <ul className="text-sm">
                          {l.listings.map((p) => (
                            <li key={p.id}>
                              {p.title}{" "}
                              <span className="text-muted-foreground">
                                ({p.city}, {p.status.toLowerCase()})
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                      <div className="flex flex-wrap items-start gap-2">
                        <ActionButton
                          variant="primary"
                          run={() => act(api("POST", `/admin/landlords/${l.landlordProfileId}/review`, OkSchema, { approve: true }))}
                        >
                          Approve
                        </ActionButton>
                        <NotesAction
                          label="Reject"
                          required
                          placeholder="Why? The landlord is shown this and told how to fix it."
                          run={(notes) => act(api("POST", `/admin/landlords/${l.landlordProfileId}/review`, OkSchema, { approve: false, notes }))}
                        />
                      </div>
                    </CardBody>
                  </Card>
                ))
              )}
            </section>

            <section className="space-y-3">
              <h2 className="text-lg font-semibold">Listings asking for the badge ({v.pendingListings.length})</h2>
              {v.pendingListings.length === 0 ? (
                <EmptyState icon={BadgeCheck} title="No requests" />
              ) : (
                v.pendingListings.map((p) => (
                  <Card key={p.verificationId}>
                    <CardBody className="flex flex-wrap items-start justify-between gap-3 p-5">
                      <div>
                        <a href={`${config.siteUrl}/listing/${p.slug}`} target="_blank" rel="noreferrer" className="font-semibold text-primary hover:underline">
                          {p.title}
                        </a>
                        <p className="text-sm text-muted-foreground">
                          {p.city}, {p.province} · asked {day(p.requestedAt)} by {p.submittedBy ?? "the landlord"}
                        </p>
                      </div>
                      <div className="flex flex-wrap items-start gap-2">
                        <ActionButton
                          variant="primary"
                          run={() => act(api("POST", `/admin/listing-verifications/${p.verificationId}/decide`, OkSchema, { approve: true }))}
                        >
                          Verify
                        </ActionButton>
                        <NotesAction
                          label="Decline"
                          placeholder="Optional note for the landlord"
                          run={(notes) =>
                            act(api("POST", `/admin/listing-verifications/${p.verificationId}/decide`, OkSchema, { approve: false, notes: notes || undefined }))
                          }
                        />
                      </div>
                    </CardBody>
                  </Card>
                ))
              )}
            </section>

            <section className="grid gap-6 md:grid-cols-2">
              <div className="space-y-2">
                <h2 className="font-semibold">Verified landlords</h2>
                {v.verifiedLandlords.map((l) => (
                  <div
                    key={l.landlordProfileId}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border px-3 py-2 text-sm"
                  >
                    <span>
                      {l.fullName ?? l.email} <span className="text-muted-foreground">· {day(l.reviewedAt)}</span>
                    </span>
                    <NotesAction
                      label="Withdraw"
                      placeholder="Optional reason"
                      run={async (notes) => {
                        const r = await api("POST", `/admin/landlords/${l.landlordProfileId}/revoke`, RevokeReplySchema, { notes: notes || undefined });
                        window.alert(
                          r.stillVerified ? "Withdrawn, but they stay verified through their phone." : `Withdrawn. ${r.pausedListings} live listing(s) paused.`,
                        );
                        await state.reload();
                      }}
                    />
                  </div>
                ))}
              </div>
              <div className="space-y-2">
                <h2 className="font-semibold">Verified listings</h2>
                {v.verifiedListings.map((p) => (
                  <div key={p.propertyId} className="flex items-center justify-between gap-2 rounded-md border border-border px-3 py-2 text-sm">
                    <span>
                      {p.title} <span className="text-muted-foreground">· {day(p.verifiedAt)}</span>
                    </span>
                    <ActionButton
                      variant="ghost"
                      confirm={`Remove the verified badge from "${p.title}"?`}
                      run={() => act(api("POST", `/admin/listings/${p.propertyId}/revoke-verification`, OkSchema))}
                    >
                      Revoke
                    </ActionButton>
                  </div>
                ))}
              </div>
            </section>

            <section className="space-y-2">
              <h2 className="font-semibold">Renters' stay confirmations ({v.stayConfirmations.length})</h2>
              {v.stayConfirmations.map((s) => (
                <div key={s.id} className="rounded-md border border-border px-3 py-2 text-sm">
                  <Badge tone={s.matchesListing === false ? "warning" : "success"}>{s.matchesListing === false ? "did not match" : "matched"}</Badge> {s.title}{" "}
                  <span className="text-muted-foreground">
                    · {s.by ?? "a renter"} · {day(s.createdAt)}
                  </span>
                  {s.notes && <p className="mt-1 text-muted-foreground">{s.notes}</p>}
                </div>
              ))}
            </section>
          </>
        )}
      </Loaded>
    </div>
  );
}
